import { signal } from '@preact/signals'
import type { ReadonlySignal } from '@preact/signals'
import { parseServerTimestamp } from '@/utils/time'
import { withClientDeadline } from '../polls/pollStay'
import type {
    RoomActivePoll,
    RoomJoin,
    RoomMessage,
    RoomModeratorState,
    RoomState,
    WebinarRoomMeDto,
    WebinarRoomPlaybackDto,
    WebinarRoomPlayerState,
    WebinarRoomPollViewDto,
    WebinarRoomRole,
} from '../types'

/**
 * Whether the page's realtime link is up. `connecting` is the first attempt only; every attempt after one fails or
 * the link drops is `reconnecting`. `stopped` is final: the API refused the token, so retrying cannot help. It says
 * nothing about the video, which plays from Mux whatever this is.
 */
export type RoomConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'stopped'

/** What the player on the stage last said it was doing, for the heartbeat. */
export interface RoomPlayerReport {
    state: WebinarRoomPlayerState
    positionSeconds: number | null
}

/** Everything one page knows about its room. */
export interface RoomSnapshot {
    role: WebinarRoomRole
    me: WebinarRoomMeDto
    room: RoomState
    playback: WebinarRoomPlaybackDto
    lastLivePositionSeconds: number | null
    /** Oldest first. A viewer has only what is visible to everyone plus their own; a moderator has everything. */
    messages: RoomMessage[]
    /** The web's polls with their times, for moderators and presenters. Empty for viewers. */
    scheduledPolls: WebinarRoomPollViewDto[]
    /** People, presenters, moderators and poll runs: moderators and presenters only, null until loaded. */
    moderator: RoomModeratorState | null
    moderatorLoadFailed: boolean
    /** Message id → the other moderator answering it now. */
    answering: Record<string, string>
    connection: RoomConnectionStatus
    player: RoomPlayerReport
}

export type RoomAction =
    | { type: 'rejoined'; join: RoomJoin }
    | { type: 'roomStateChanged'; room: RoomState }
    | { type: 'historyLoaded'; messages: RoomMessage[] }
    | { type: 'messageUpserted'; message: RoomMessage }
    | { type: 'messageRemoved'; messageId: string }
    | { type: 'answering'; messageId: string; name: string; active: boolean }
    | { type: 'pollLaunched'; poll: RoomActivePoll }
    | { type: 'pollClosed'; pollId: string }
    | { type: 'pollTally'; pollId: string; counts: number[]; total: number }
    | { type: 'pollAnswered'; poll: RoomActivePoll }
    | { type: 'moderatorStateLoaded'; state: RoomModeratorState }
    | { type: 'moderatorStateFailed' }
    | { type: 'connection'; status: RoomConnectionStatus }
    | { type: 'player'; report: RoomPlayerReport }

/** Moderators and presenters see every message, hidden ones too, and hear about them on their own group. */
export function runsTheRoom(role: WebinarRoomRole): boolean {
    return role === 'Moderator' || role === 'Presenter'
}

const createdMs = (message: RoomMessage) => parseServerTimestamp(message.CreatedUtc) ?? 0

function sortedOldestFirst(messages: RoomMessage[]): RoomMessage[] {
    return [...messages].sort((a, b) => createdMs(a) - createdMs(b))
}

/**
 * A room state broadcast is the same for everyone, so its poll carries nobody's answer. The answer this page already
 * gave, and the counts it was shown for it, stay until the poll itself changes.
 */
function keepingMyPollAnswer(current: RoomState, incoming: RoomState): RoomState {
    const mine = current.ActivePoll
    const next = incoming.ActivePoll
    if (!mine || !next || mine.PollId !== next.PollId || mine.MyAnswerIndex == null || next.MyAnswerIndex != null) return incoming
    return {
        ...incoming,
        ActivePoll: { ...next, MyAnswerIndex: mine.MyAnswerIndex, Counts: next.Counts ?? mine.Counts, Total: next.Total ?? mine.Total },
    }
}

/** A question answered, dismissed or gone back to chat has nobody answering it, whoever never said they stopped. */
function withoutSettledAnswering(answering: Record<string, string>, message: RoomMessage): Record<string, string> {
    if (!(message.Id in answering) || message.QuestionState === 'Open') return answering
    const rest = { ...answering }
    delete rest[message.Id]
    return rest
}

/** The room as it arrived at `receivedMs`, its poll's close timed on this page's clock from then. */
function received(room: RoomState, receivedMs: number): RoomState {
    return room.ActivePoll ? { ...room, ActivePoll: withClientDeadline(room.ActivePoll, receivedMs) } : room
}

export function initialSnapshot(join: RoomJoin, receivedMs: number = Date.now()): RoomSnapshot {
    return {
        role: join.Role,
        me: join.Me,
        room: received(join.Room, receivedMs),
        playback: join.Playback,
        lastLivePositionSeconds: join.LastLivePositionSeconds ?? null,
        messages: sortedOldestFirst(join.Messages),
        scheduledPolls: join.Polls,
        moderator: null,
        moderatorLoadFailed: false,
        answering: {},
        connection: 'connecting',
        player: { state: 'Slate', positionSeconds: null },
    }
}

/**
 * Launches that race are settled in one write but broadcast from separate requests, so their events can land out of
 * order: a poll launched before the one showing has already been closed by it.
 */
function launchedBeforeShowing(incoming: RoomActivePoll, showing: RoomActivePoll | null | undefined): boolean {
    if (!showing || showing.PollId === incoming.PollId) return false
    const incomingMs = parseServerTimestamp(incoming.LaunchedUtc)
    const showingMs = parseServerTimestamp(showing.LaunchedUtc)
    return incomingMs !== null && showingMs !== null && incomingMs < showingMs
}

function withActivePoll(state: RoomSnapshot, update: (poll: RoomActivePoll) => RoomActivePoll | null, pollId: string): RoomSnapshot {
    const active = state.room.ActivePoll
    if (!active || active.PollId !== pollId) return state
    return { ...state, room: { ...state.room, ActivePoll: update(active) } }
}

/**
 * `receivedMs` is this page's clock as the action arrives: every room state and launched poll carries the server's
 * seconds left on the poll, and its close is timed from then (`withClientDeadline`).
 */
export function roomReducer(state: RoomSnapshot, action: RoomAction, receivedMs: number = Date.now()): RoomSnapshot {
    switch (action.type) {
        // The page's own join asked for again: the room as it is now, and the playback, whose tokens only a join mints.
        // The messages are the caller's to replace, and the rest is this page's own.
        case 'rejoined':
            return {
                ...state,
                room: keepingMyPollAnswer(state.room, received(action.join.Room, receivedMs)),
                playback: action.join.Playback,
                lastLivePositionSeconds: action.join.LastLivePositionSeconds ?? null,
            }

        case 'roomStateChanged':
            return { ...state, room: keepingMyPollAnswer(state.room, received(action.room, receivedMs)) }

        case 'historyLoaded':
            return { ...state, messages: sortedOldestFirst(action.messages) }

        case 'messageUpserted': {
            const index = state.messages.findIndex(message => message.Id === action.message.Id)
            const answering = withoutSettledAnswering(state.answering, action.message)
            if (index === -1) return { ...state, answering, messages: sortedOldestFirst([...state.messages, action.message]) }
            const messages = [...state.messages]
            messages[index] = action.message
            return { ...state, answering, messages }
        }

        // A message stops being visible to everyone. Its author keeps it, unaware; moderators keep it, marked hidden by
        // the copy their own group gets.
        case 'messageRemoved': {
            if (runsTheRoom(state.role)) return state
            const messages = state.messages.filter(message => message.Id !== action.messageId || message.AuthorKey === state.me.AuthorKey)
            return messages.length === state.messages.length ? state : { ...state, messages }
        }

        // The hub sends it to every moderator, this one included: only someone else answering is news here.
        case 'answering': {
            if (action.name === state.me.Name) return state
            const answering = { ...state.answering }
            if (action.active) answering[action.messageId] = action.name
            else delete answering[action.messageId]
            return { ...state, answering }
        }

        case 'pollLaunched':
            if (launchedBeforeShowing(action.poll, state.room.ActivePoll)) return state
            return { ...state, room: { ...state.room, ActivePoll: withClientDeadline(action.poll, receivedMs) } }

        case 'pollClosed':
            return withActivePoll(state, () => null, action.pollId)

        // Each answer's request reads the tally after its own write, so two at once can land the lower count last.
        // Answers only grow while a poll runs.
        case 'pollTally': {
            const showing = state.room.ActivePoll
            if (showing?.PollId === action.pollId && showing.Total != null && action.total < showing.Total) return state
            return withActivePoll(state, poll => ({ ...poll, Counts: action.counts, Total: action.total }), action.pollId)
        }

        // The answer's reply counts its seconds left after a round trip the poll's own arrival did not wait for, so the
        // close already timed stands.
        case 'pollAnswered':
            return withActivePoll(state, active => ({ ...action.poll, closesAtMs: active.closesAtMs }), action.poll.PollId)

        case 'moderatorStateLoaded':
            return { ...state, moderator: action.state, moderatorLoadFailed: false, room: received(action.state.Room, receivedMs) }

        case 'moderatorStateFailed':
            return { ...state, moderatorLoadFailed: true }

        case 'connection':
            return state.connection === action.status ? state : { ...state, connection: action.status }

        case 'player':
            return { ...state, player: action.report }
    }
}

export interface RoomStore {
    readonly state: ReadonlySignal<RoomSnapshot>
    dispatch(action: RoomAction): void
}

/** One room's state as a signal: components that read `state.value` re-render when it changes. */
export function createRoomStore(join: RoomJoin): RoomStore {
    const state = signal(initialSnapshot(join))
    return {
        state,
        dispatch: action => { state.value = roomReducer(state.value, action) },
    }
}
